// Full-screen record editor overlay for the compact (mobile-style) interface —
// the compact peer of TxnScreen's EditorModal. Shell only: the top bar (back /
// title / mode badge / Save) plus a scrollable body; the host composes the body
// (ValidationBanner + FormRenderer with layout="compact" + stacked child
// sections) as children, exactly as it does for the desktop modal. ESC closes
// and Ctrl+S saves, gated on being the topmost layer so a stacked lookup /
// child-row editor handles its own keys first.

import { useEffect, useRef } from 'react'
import type { ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { ChevronLeft, FilePlus, Minus, Save } from 'lucide-react'
import { useKeyboardShortcuts, useShortcutLayer } from '../keyboard'
import { FormModeBadge } from '../FormRenderer'
import { tw } from '../../testHooks'

interface Props {
  open: boolean
  title: string
  mode: 'add' | 'edit' | 'view'
  // Why the record is read-only — shown as the mode badge's hover tooltip.
  lockReason?: string
  txnId?: string | null
  dirty: boolean
  busy: boolean
  onRequestClose: () => void
  onSave?: () => void
  onSaveAndClose?: () => void
  children: ReactNode
  /** Flags raised on the record (additional attributes), by the mode badge. */
  badges?: ReactNode
  /** An open editor the Run shell keeps alive while minimised: stays MOUNTED
   *  (everything typed survives) but is not shown and takes no keys. */
  hidden?: boolean
  /** The shell's Minimise — present only for such a kept-alive editor. */
  onMinimise?: () => void
  /** The editor layout switch (Standard / Simple / Document / Compact), drawn
   *  under the top bar where a phone has room for it. */
  layoutSwitch?: ReactNode
}

export default function CompactFormShell({
  open, title, mode, lockReason, txnId, dirty, busy,
  onRequestClose, onSave, onSaveAndClose, children, badges, hidden = false, onMinimise,
  layoutSwitch,
}: Props) {
  const viewOnly = mode === 'view'
  // Kept alive but minimised: mounted, not interactive.
  const active = open && !hidden

  // ESC closes (only when open).
  useEffect(() => {
    if (!active) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { e.stopPropagation(); onRequestClose() }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [active, onRequestClose])

  // Ctrl+S saves, gated on the topmost layer (a stacked lookup / child editor
  // whose DOM nests in this shell saves itself first).
  const cardRef = useRef<HTMLDivElement>(null)
  const { isTopLayer } = useShortcutLayer(active)
  useKeyboardShortcuts(
    [{ combo: 'ctrl+s', run: () => { if (!busy && !viewOnly && isTopLayer()) onSave?.() } }],
    { enabled: active, scopeRef: cardRef },
  )

  if (!open) return null

  const node = (
    <div
      ref={cardRef}
      data-tw-dialog={hidden ? undefined : 'editor'}
      data-tw-mode={mode}
      aria-hidden={hidden || undefined}
      style={{
        // Full-screen. Sits at the editor-modal layer (9430); lookup (9440) and
        // child-form (9450) modals still stack above.
        position: 'fixed', inset: 0, zIndex: 9430,
        background: 'var(--color-bg)', color: 'var(--color-text)',
        display: hidden ? 'none' : 'flex', flexDirection: 'column',
      }}
    >
      <div style={{
        display: 'flex', alignItems: 'center', gap: 10,
        padding: '10px 14px',
        borderBottom: '1px solid var(--color-border)',
        background: 'var(--color-bg)',
      }}>
        <button className="btn btn-ghost btn-sm" onClick={onRequestClose} disabled={busy} title="Back" {...tw('action', 'close')} style={{ padding: 5 }}>
          <ChevronLeft size={18} />
        </button>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1, minWidth: 0 }}>
          <strong style={{ fontSize: '0.9825rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {title}
          </strong>
          <FormModeBadge mode={mode} lockReason={lockReason} />
          {badges}
          {dirty && (
            <span style={{ fontSize: '0.8125rem', color: '#eab308', fontWeight: 600, whiteSpace: 'nowrap' }}>● unsaved</span>
          )}
        </div>
        {onMinimise && (
          <button className="btn btn-ghost btn-sm" onClick={onMinimise} disabled={busy}
                  title="Minimise — keep this record open and pick it up from Open editors"
                  aria-label="Minimise" {...tw('action', 'shell-minimise')}
                  style={{ display: 'inline-flex', alignItems: 'center', padding: 5 }}>
            <Minus size={16} />
          </button>
        )}
        {!viewOnly && (
          <div style={{ display: 'flex', gap: 6 }}>
            {onSave && (
              <button className="btn btn-ghost btn-sm" onClick={onSave} disabled={busy} title="Save and continue editing (Ctrl+S)" {...tw('action', 'save')} style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                <Save size={15} /> Save
              </button>
            )}
            {onSaveAndClose && (
              <button className="btn btn-primary btn-sm" onClick={onSaveAndClose} disabled={busy} title="Save and close" {...tw('action', 'save_close')} style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                <FilePlus size={15} /> Save &amp; Close
              </button>
            )}
          </div>
        )}
      </div>

      {layoutSwitch && (
        <div style={{
          display: 'flex', justifyContent: 'flex-end', padding: '6px 14px',
          borderBottom: '1px solid var(--color-border)', overflowX: 'auto',
        }}>{layoutSwitch}</div>
      )}

      {/* Body: the host composes ValidationBanner + FormRenderer + child
          sections. Centred with a readable max width on wider phones/tablets. */}
      <div style={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
        <div style={{ maxWidth: 720, margin: '0 auto', width: '100%' }}>
          {txnId != null && txnId !== '' && (
            <div style={{ padding: '6px 14px 0', color: 'var(--color-text-secondary)', fontSize: '0.8125rem' }}>
              <code style={{ background: 'var(--color-bg-secondary)', padding: '2px 6px', borderRadius: 3 }}>
                {txnId.slice(0, 18)}{txnId.length > 18 ? '…' : ''}
              </code>
            </div>
          )}
          {children}
        </div>
      </div>
    </div>
  )

  return createPortal(node, document.body)
}
